import { useMemo, useState } from 'react';
import type { ProcessRow } from '../../../../services/databaseMonitor.service';
import { Button } from './ui';

type SortKey = keyof ProcessRow;
type Dir = 'asc' | 'desc';

interface Props {
  rows: ProcessRow[];
  onKill: (row: ProcessRow) => void;
}

const columns: { key: SortKey; label: string; mono?: boolean; right?: boolean; maxChars?: number }[] = [
  { key: 'id', label: 'ID', right: true },
  { key: 'user', label: 'User' },
  { key: 'host', label: 'Host', mono: true, maxChars: 20 },
  { key: 'db', label: 'DB' },
  { key: 'command', label: 'Command' },
  { key: 'time', label: 'Time (s)', right: true },
  { key: 'state', label: 'State' },
  { key: 'info', label: 'Query', mono: true, maxChars: 100 },
];

export function ProcessListTable({ rows, onKill }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('time');
  const [dir, setDir] = useState<Dir>('desc');

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return dir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av);
      const bs = String(bv);
      return dir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return copy;
  }, [rows, sortKey, dir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setDir(dir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setDir(key === 'time' || key === 'id' ? 'desc' : 'asc');
    }
  };

  return (
    <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-lg w-full">
      <table className="w-full text-sm">
        <thead className="bg-slate-100 dark:bg-slate-900/80 text-slate-600 dark:text-slate-400">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                onClick={() => toggleSort(c.key)}
                className={
                  'px-3 py-2 text-left font-medium select-none cursor-pointer whitespace-nowrap ' +
                  (c.right ? 'text-right ' : '')
                }
              >
                {c.label}
                {sortKey === c.key && <span className="ml-1 opacity-70">{dir === 'asc' ? '▲' : '▼'}</span>}
              </th>
            ))}
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td colSpan={columns.length + 1} className="px-3 py-8 text-center text-slate-500 dark:text-slate-500">
                No rows
              </td>
            </tr>
          )}
          {sorted.map((r) => (
            <tr key={r.id} className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/60">
              {columns.map((c) => {
                const v = r[c.key];
                const full = v == null ? null : c.key === 'info' ? compactWhitespace(String(v)) : String(v);
                const display = c.maxChars != null && full != null ? truncate(full, c.maxChars) : full;
                return (
                  <td
                    key={c.key}
                    className={
                      'px-3 py-1.5 align-top whitespace-nowrap ' +
                      (c.right ? 'text-right ' : '') +
                      (c.mono ? 'text-xs ' : '')
                    }
                    title={full != null && display !== full ? full : undefined}
                  >
                    {display == null ? <span className="text-slate-400 dark:text-slate-600">—</span> : display}
                  </td>
                );
              })}
              <td className="px-3 py-1.5 text-right">
                <Button variant="danger" onClick={() => onKill(r)}>
                  Kill
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function compactWhitespace(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + '…' : s;
}
